import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import moment, { Moment } from 'moment';
import 'moment/locale/it';
import 'moment/locale/el';
import { Municipality, MunicipalityService } from '../core/municipality.service';

interface ConfirmationState {
  firstName?: string;
  lastName?: string;
  municipality?: string;
  appointment?: {
    from: string | Moment;
    until: string | Moment;
    waitingRoom?: string;
    specialty?: string;
  };
}

@Component({
  selector: 'app-appointment-confirmation',
  standalone: true,
  templateUrl: './appointment-confirmation.component.html',
  imports: [TranslatePipe, NgIf],
})
export class AppointmentConfirmationComponent implements OnInit, AfterViewInit, OnDestroy {
  state?: ConfirmationState;
  municipality: Municipality | null = null;
  printedAt = moment();
  countdown = 10;
  private timer?: any;

  @ViewChild('receipt') receipt?: ElementRef<HTMLElement>;
  private pageStyle?: HTMLStyleElement;

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private municipalityService: MunicipalityService,
  ) {}

  ngOnInit(): void {
    // Retrieve navigation state (if user refreshed, it will be empty)
    this.state = JSON.parse(sessionStorage.getItem('appointmentState') ?? 'null') ?? undefined;

    if (!this.state?.appointment) {
      // No data, go home
      this.router.navigate(['/services']);
      sessionStorage.removeItem('appointmentState');
      return;
    }

    moment.locale(this.lang);
    this.municipality = this.municipalityService.getCurrent();

    this.timer = setInterval(() => {
      this.countdown -= 1;
      if (this.countdown <= 0) {
        this.goHome();
      }
    }, 1000);
  }

  ngAfterViewInit(): void {
    if (this.state?.appointment) {
      // Auto-print the ticket once its logos are loaded and its height is known
      void this.printTicket();
    }
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
    this.pageStyle?.remove();
  }

  private get lang(): string {
    return this.translateService.currentLang || this.translateService.defaultLang || 'it';
  }

  get municipalityName(): string {
    return this.municipality?.label || this.municipality?.fallbackLabel || this.state?.municipality || '-';
  }

  // Screen: "Giovedì 15 Ottobre 2026" (en: "Thursday, October 15, 2026")
  formatDate(value?: string | Moment): string {
    if (!value) return '';
    const text = moment(value).format(this.lang === 'en' ? 'dddd, MMMM D, YYYY' : 'dddd D MMMM YYYY');
    return text.replace(/(^|\s)\S/g, c => c.toUpperCase());
  }

  // Always 24-hour, no AM/PM
  formatTime(value?: string | Moment): string {
    if (!value) return '';
    return moment(value).format('HH:mm');
  }

  // Ticket date block: "GIOVEDÌ" / "15 OTTOBRE" / "2026"
  ticketDate(part: 'weekday' | 'dayMonth' | 'year'): string {
    const from = moment(this.state?.appointment?.from);
    if (part === 'weekday') return from.format('dddd').toUpperCase();
    if (part === 'dayMonth') return from.format(this.lang === 'en' ? 'MMMM D' : 'D MMMM').toUpperCase();
    return from.format('YYYY');
  }

  // Page height follows the ticket content: the ticket is laid out off-screen at print width,
  // measured, and an @page rule with that height is added just before printing.
  private async printTicket(): Promise<void> {
    const el = this.receipt?.nativeElement;
    if (!el) return;
    await Promise.all(Array.from(el.querySelectorAll('img')).map(img => img.decode().catch(() => undefined)));
    const heightMm = Math.ceil((el.getBoundingClientRect().height * 25.4) / 96) + 2;
    this.pageStyle = document.createElement('style');
    this.pageStyle.textContent = `@page { size: 80mm ${heightMm}mm; margin: 0; }`;
    document.head.appendChild(this.pageStyle);
    window.print();
  }

  goHome(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
    sessionStorage.removeItem('appointmentState');
    this.router.navigate(['/services']);
  }
}
